import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from 'react';
import { getDisplayStoreNumber } from '../../../utils/storeNumber';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Box,
  Button,
  Alert,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Checkbox,
  ListItemText,
  Stack,
  SvgIcon,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  CalendarToday as CalendarIcon,
  PlayArrow as ActivateIcon,
  Pause as PauseIcon,
  SendRounded as TriggerIcon,
} from '@mui/icons-material';
import { Plus } from '../../../assets';
import {
  Auditor,
  AuditSchedule,
  AuditTemplate,
  Region,
  ScheduleForm,
  Store,
} from '../../../pages/Compliance/Interfaces/complicanceInterfaces';
import { complianceService } from '../../../app/services/api/compliance/complianceService';
import { UserContext } from '../../../components/shared/useUser';
import PageArea from '../../../components/shared/PageArea';
import PageTitle from '../../../components/shared/PageTitle';
import { ConfirmationDeleteDialog } from '../../../components/shared/dialogs/ConfirmationDeleteDialog';
import { Section } from '../../../components/ui/Section';
import { Pill } from '../../../components/shared/Pill';
import { EmptyTable } from '../../../components/shared/EmptyTable';
import CustomPagination from '../../../components/forms/Pagination';
import CronExpressionPicker from '../../../components/forms/CronExpressionPicker';

const formatDate = (dateStr?: string | null, locale?: string): string => {
  if (!dateStr) return '\u2014';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '\u2014';
    return date.toLocaleDateString(locale);
  } catch {
    return '\u2014';
  }
};

const AuditSchedules: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation('complianceAudits');
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);
  const { selectedStore } = useContext(UserContext);
  const localeLanguage = navigator.languages?.[0] || navigator.language;
  // -----------------------------
  // State
  // -----------------------------
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [schedules, setSchedules] = useState<AuditSchedule[]>([]);
  const [templates, setTemplates] = useState<AuditTemplate[]>([]);
  const [filteredStores, setFilteredStores] = useState<Store[]>([]);
  const [selectedStores, setSelectedStores] = useState<Store[]>([]);
  const [storesLoading, setStoresLoading] = useState(false);
  const [filteredRegions, setFilteredRegions] = useState<Region[]>([]);
  const [filteredAuditors, setFilteredAuditors] = useState<Auditor[]>([]);
  const [auditorsLoading, setAuditorsLoading] = useState(false);
  const [templateDataLoading, setTemplateDataLoading] = useState(false);
  const [page, setPage] = useState(0);
  const templateFranchiseIdsRef = useRef<number[]>([]);
  const storeSearchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const auditorDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const rowsPerPage = 10;
  const maxPages = () => Math.ceil(schedules.length / rowsPerPage);
  const paginatedSchedules = schedules.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  // -----------------------------
  // Dialogs
  // -----------------------------
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [triggerDialogOpen, setTriggerDialogOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<ScheduleForm | null>(null);
  const [triggerSchedule, setTriggerSchedule] = useState<AuditSchedule | null>(null);
  const [triggering, setTriggering] = useState(false);

  const resetForm = () => {
    setForm({
      templateId: 0,
      assignmentType: 'all',
      storeNumbers: [],
      regionIds: [],
      cronExpression: '0 0 0 ? * * *',
      isActive: true,
      isAdHoc: false,
      assignToUserIds: [],
      notifyOnCreate: true,
    });
  };

  // -----------------------------
  // Form
  // -----------------------------
  const [form, setForm] = useState<ScheduleForm>({
    templateId: 0,
    assignmentType: 'all',
    storeNumbers: [],
    regionIds: [],
    cronExpression: '0 0 0 ? * * *',
    isActive: true,
    isAdHoc: false,
    assignToUserIds: [],
    notifyOnCreate: true,
  });

  useEffect(() => {
    setPage(0);
  }, [selectedStore?.storeNumber]);

  useEffect(() => {
    loadData();
  }, [page, selectedStore?.storeNumber]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const schedulesData = await complianceService.auditSchedules.getAll(undefined, undefined, true, selectedStore?.storeNumber);

      setSchedules(schedulesData);
    } catch (err) {
      setError(t('common.failedToLoadData'));
    } finally {
      setLoading(false);
    }
  };
  const publishedTemplates = useMemo(() => {
    return templates.filter((tpl) => tpl.isPublished);
  }, [templates]);

  const selectedTemplateFranchises = useMemo(() => {
    if (form.templateId === 0) return [];
    const tpl = templates.find(t => t.id === form.templateId);
    return tpl?.franchises || [];
  }, [templates, form.templateId]);

  const loadDataForTemplate = useCallback(async (templateId: number, templatesSource?: AuditTemplate[]) => {
    setFilteredStores([]);
    setSelectedStores([]);
    setTemplateDataLoading(true);
    try {
      const tplList = templatesSource || templates;
      const template = tplList.find(t => t.id === templateId);
      const franchiseIds = template?.franchises?.map(f => f.franchiseId) || [];
      templateFranchiseIdsRef.current = franchiseIds;

      const [regionsData, auditorsData] = await Promise.all([
        complianceService.complianceRegions.getAll(franchiseIds.length > 0 ? franchiseIds : undefined),
        complianceService.auditors.getAll(franchiseIds.length > 0 ? franchiseIds : undefined),
      ]);
      setFilteredRegions(regionsData);
      setFilteredAuditors(auditorsData);
    } catch {
      setFilteredRegions([]);
      setFilteredAuditors([]);
    } finally {
      setTemplateDataLoading(false);
    }
  }, [templates]);

  const loadAuditorsForRegions = useCallback(async (regionIds: string[], immediate = false) => {
    if (auditorDebounceRef.current) {
      clearTimeout(auditorDebounceRef.current);
    }
    setAuditorsLoading(true);
    if (!regionIds.length) {
      setFilteredAuditors([]);
      setAuditorsLoading(false);
      return;
    }
    const doLoad = async () => {
      try {
        const numericRegionIds = regionIds.map(Number);
        const auditorsData = await complianceService.auditors.getAll(undefined, numericRegionIds);
        setFilteredAuditors(auditorsData);
      } catch {
        setFilteredAuditors([]);
      } finally {
        setAuditorsLoading(false);
      }
    };
    if (immediate) {
      await doLoad();
    } else {
      auditorDebounceRef.current = setTimeout(doLoad, 500);
    }
  }, []);

  const loadAuditorsForStores = useCallback(async (storeNumbers: string[], immediate = false) => {
    if (auditorDebounceRef.current) {
      clearTimeout(auditorDebounceRef.current);
    }
    setAuditorsLoading(true);
    if (!storeNumbers.length) {
      setFilteredAuditors([]);
      setAuditorsLoading(false);
      return;
    }
    const doLoad = async () => {
      try {
        const auditorsData = await complianceService.auditors.getAll(undefined, undefined, storeNumbers);
        setFilteredAuditors(auditorsData);
      } catch {
        setFilteredAuditors([]);
      } finally {
        setAuditorsLoading(false);
      }
    };
    if (immediate) {
      await doLoad();
    } else {
      auditorDebounceRef.current = setTimeout(doLoad, 500);
    }
  }, []);

  const searchStores = useCallback(async (search: string) => {
    const franchiseIds = templateFranchiseIdsRef.current;
    try {
      setStoresLoading(true);
      const hasSearch = search && search.trim().length > 0;
      const storesData = await complianceService.complianceStores.getAll(false, franchiseIds.length > 0 ? franchiseIds : undefined, hasSearch ? search : undefined, hasSearch ? 20 : undefined);
      setFilteredStores(storesData);
    } catch {
      setFilteredStores([]);
    } finally {
      setStoresLoading(false);
    }
  }, []);

  const handleStoreSearchInput = useCallback((_e: React.SyntheticEvent, value: string) => {
    if (storeSearchTimerRef.current) clearTimeout(storeSearchTimerRef.current);
    storeSearchTimerRef.current = setTimeout(() => {
      searchStores(value);
    }, 300);
  }, [searchStores]);


  const handleTemplateChange = (templateId: number) => {
    setForm(prev => ({
      ...prev,
      templateId,
      storeNumbers: [],
      regionIds: [],
      assignToUserIds: [],
    }));
    setSelectedStores([]);
    if (templateId > 0) {
      loadDataForTemplate(templateId);
    } else {
      templateFranchiseIdsRef.current = [];
      setFilteredRegions([]);
      setFilteredAuditors([]);
    }
  };

  const handleAddSchedule = async () => {
    resetForm();
    setSelectedSchedule(null);
    templateFranchiseIdsRef.current = [];
    setSelectedStores([]);
    setFilteredStores([]);
    setFilteredRegions([]);
    setFilteredAuditors([]);
    try {
      const templatesData = await complianceService.auditTemplates.getAll(undefined, false, selectedStore?.storeNumber);
      setTemplates(templatesData);
    } catch {
      setTemplates([]);
    }
    setScheduleDialogOpen(true);
  };

  const handleEditSchedule = async (schedule: AuditSchedule) => {
    
    const formData: ScheduleForm = {
      id: schedule.id,
      templateId: schedule.templateId,
      assignmentType: schedule.assignmentType || 'all',
      storeNumbers: schedule.storeNumbers || [],
      regionIds: schedule.regionIds || [],
      cronExpression: schedule.cronExpression || '',
      isActive: schedule.isActive,
      isAdHoc: schedule.isAdHoc ?? false,
      assignToUserIds: schedule.assignToUserIds || [],
      notifyOnCreate: schedule.notifyOnCreate ?? true,
      templateName: schedule.templateName,
      storeName: schedule.storeName,
      nextScheduledDate: schedule.nextRun || schedule.nextScheduledDate,
    };

    setSelectedSchedule(formData);
    setForm(formData);

    try {
      const templatesData = await complianceService.auditTemplates.getAll(undefined, false, selectedStore?.storeNumber);
      setTemplates(templatesData);
      await loadDataForTemplate(schedule.templateId, templatesData);

      if (schedule.assignmentType === 'regions' && schedule.regionIds && schedule.regionIds.length > 0) {
        await loadAuditorsForRegions(schedule.regionIds, true);
      } else if (schedule.assignmentType === 'stores' && schedule.storeNumbers && schedule.storeNumbers.length > 0) {
        await loadAuditorsForStores(schedule.storeNumbers, true);
      }

      if (schedule.storeNumbers && schedule.storeNumbers.length > 0) {
        const tpl = templatesData.find((t: AuditTemplate) => t.id === schedule.templateId);
        const franchiseIds = tpl?.franchises?.map((f: { franchiseId: number }) => f.franchiseId) || [];
        complianceService.complianceStores.getAll(false, franchiseIds.length > 0 ? franchiseIds : undefined, undefined, undefined)
          .then(allStores => {
            const assignedSet = new Set(schedule.storeNumbers);
            setSelectedStores(allStores.filter(s => assignedSet.has(s.storeNumber)));
          })
          .catch(() => setSelectedStores([]));
      } else {
        setSelectedStores([]);
      }
    } catch {
      setTemplates([]);
      setFilteredRegions([]);
      setFilteredAuditors([]);
    }

    setScheduleDialogOpen(true);
  };

  const handleSaveSchedule = async () => {
    if (form.templateId === 0) {
      setError(t('schedules.validationSelectTemplate'));
      return;
    }

    if (form.assignmentType === 'stores' && form.storeNumbers.length === 0) {
      setError(t('schedules.validationSelectStores'));
      return;
    }

    if (form.assignmentType === 'regions' && form.regionIds.length === 0) {
      setError(t('schedules.validationSelectRegions'));
      return;
    }

    if (!form.isAdHoc && !form.cronExpression) {
      setError(t('schedules.validationDefineCron'));
      return;
    }

    if (form.assignToUserIds.length === 0) {
      setError(t('schedules.validationSelectAuditor'));
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const payload = {
        templateId: form.templateId,
        cronExpression: form.isAdHoc ? '' : form.cronExpression,
        isActive: form.isAdHoc ? true : form.isActive,
        isAdHoc: form.isAdHoc,
        assignToUserIds: form.assignToUserIds,
        notifyOnCreate: form.notifyOnCreate,
        storeNumbers: form.storeNumbers,
        regionIds: form.regionIds,
        assignmentType: form.assignmentType,
      };

      if (form.id) {
        await complianceService.auditSchedules.update(form.id, payload, selectedStore?.storeNumber);
        setSuccess(t('schedules.updateSuccess'));
      } else {
        await complianceService.auditSchedules.create(payload, selectedStore?.storeNumber);
        setSuccess(t('schedules.createSuccess'));
      }

      setScheduleDialogOpen(false);
      resetForm();
      setPage(0);
      loadData();
    } catch (err) {
      setError(t('schedules.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSchedule = (schedule: AuditSchedule) => {
    setSelectedSchedule({
      id: schedule.id,
      templateId: schedule.templateId,
      assignmentType: schedule.assignmentType || 'all',
      storeNumbers: schedule.storeNumbers || [],
      regionIds: schedule.regionIds || [],
      cronExpression: schedule.cronExpression || '',
      isActive: schedule.isActive,
      isAdHoc: schedule.isAdHoc ?? false,
      assignToUserIds: schedule.assignToUserIds || [],
      notifyOnCreate: schedule.notifyOnCreate ?? true,
      templateName: schedule.templateName,
      storeName: schedule.storeName,
      nextScheduledDate: schedule.nextRun || schedule.nextScheduledDate,
    });
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedSchedule) return;
    try {
      setSaving(true);
      setError(null);

      await complianceService.auditSchedules.delete(selectedSchedule.id ?? 0, selectedStore?.storeNumber);

      setSuccess(t('schedules.deleteSuccess'));
      setDeleteDialogOpen(false);
      setSelectedSchedule(null);
      setPage(0);
      loadData();
    } catch (err) {
      setError(t('schedules.deleteFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (schedule: AuditSchedule) => {
    try {
      setSaving(true);
      setError(null);

      await complianceService.auditSchedules.update(schedule.id ?? 0, {
        templateId: schedule.templateId,
        cronExpression: schedule.cronExpression || '',
        isActive: !schedule.isActive,
        isAdHoc: schedule.isAdHoc ?? false,
        assignToUserIds: schedule.assignToUserIds || [],
        notifyOnCreate: schedule.notifyOnCreate ?? true,
        storeNumbers: schedule.storeNumbers || [],
        regionIds: schedule.regionIds || [],
        assignmentType: schedule.assignmentType || 'all',
      }, selectedStore?.storeNumber);

      setSuccess(t('schedules.toggleSuccess', { action: schedule.isActive ? t('schedules.deactivate').toLowerCase() : t('schedules.activate').toLowerCase() }));
      loadData();
    } catch (err) {
      setError(t('schedules.toggleFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleTriggerSchedule = (schedule: AuditSchedule) => {
    setTriggerSchedule(schedule);
    setTriggerDialogOpen(true);
  };

  const handleConfirmTrigger = async () => {
    if (!triggerSchedule) return;
    try {
      setTriggering(true);
      setError(null);

      const auditsCreated = await complianceService.auditSchedules.trigger(triggerSchedule.id, selectedStore?.storeNumber);

      if (auditsCreated > 0) {
        setSuccess(t('schedules.triggerSuccess', { count: auditsCreated }));
      } else {
        setSuccess(t('schedules.triggerNoneCreated'));
      }

      setTriggerDialogOpen(false);
      setTriggerSchedule(null);
      loadData();
    } catch (err) {
      setError(t('schedules.triggerFailed'));
    } finally {
      setTriggering(false);
    }
  };

  const storeOptions = useMemo(() => {
    const merged = [...selectedStores];
    for (const s of filteredStores) {
      if (!merged.some(ms => ms.storeNumber === s.storeNumber)) {
        merged.push(s);
      }
    }
    return merged;
  }, [filteredStores, selectedStores]);

  // -----------------------------
  // Render
  // -----------------------------
  if (loading) {
    return (
      <PageArea>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: 8,
          }}
        >
          <CircularProgress />
        </Box>
      </PageArea>
    );
  }

  return (
    <PageArea>
      <Stack
        width="100%"
        spacing={2}
        direction={isMobile ? 'column' : 'row'}
        sx={{ justifyContent: 'space-between', mb: 3 }}
      >
        <PageTitle subTitle={t('schedules.pageSubTitle')}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CalendarIcon />
            {t('schedules.pageTitle')}
          </Box>
        </PageTitle>
        <Button
          variant="primary"
          size="lg"
          startIcon={<SvgIcon><Plus /></SvgIcon>}
          onClick={handleAddSchedule}
          disabled={saving}
        >
          {t('schedules.createSchedule')}
        </Button>
      </Stack>

      {error && <Alert severity="error"
        onClose={() => setError(null)}
        sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success"
        onClose={() => setSuccess(null)}
        sx={{ mb: 2 }}>{success}</Alert>}

      <Section>
        {schedules.length === 0 ? (
          <EmptyTable
            bodyRowHeight={40}
            headerHeight={45}
            text={t('schedules.noSchedules')}
            searchTerm=""
            onClear={() => { }}
            onAdd={handleAddSchedule}
            onAddIsCreating={false}
            addItemText={t('schedules.createSchedule')}
            onAddIsDisabled={false}
            withPaginationShown={false}
          />
        ) : (
          <>
            <Box sx={{ p: 5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="textMD"
                  fontWeight={600}>{t('schedules.pageTitle')}</Typography>
                <Pill text={t('schedules.scheduleCount', { count: schedules.length })} />
              </Box>
            </Box>

            <TableContainer sx={{ borderBottomLeftRadius: 12, borderBottomRightRadius: 12 }}>
              <Table sx={{ minWidth: 1000 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('schedules.template')}</TableCell>
                    <TableCell>{t('schedules.store')}</TableCell>
                    <TableCell>{t('schedules.nextAudit')}</TableCell>
                    <TableCell>{t('schedules.startDate')}</TableCell>
                    <TableCell>{t('schedules.endDate')}</TableCell>
                    <TableCell>{t('schedules.recurrence')}</TableCell>
                    <TableCell>{t('common.status')}</TableCell>
                    <TableCell align="right">{t('common.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedSchedules.map((schedule) => (
                    <TableRow key={schedule.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleEditSchedule(schedule)}>
                      <TableCell>{schedule.templateName}</TableCell>
                      <TableCell>{schedule.storeName}</TableCell>
                      <TableCell>{schedule.isAdHoc ? '\u2014' : formatDate(schedule.nextAuditDate, localeLanguage)}</TableCell>
                      <TableCell>{schedule.isAdHoc ? '\u2014' : formatDate(schedule.startDate, localeLanguage)}</TableCell>
                      <TableCell>{schedule.isAdHoc ? '\u2014' : (schedule.endDate ? formatDate(schedule.endDate, localeLanguage) : t('schedules.noEndDate'))}</TableCell>
                      <TableCell>{schedule.isAdHoc ? t('schedules.adHoc') : (schedule.recurrence || '\u2014')}</TableCell>
                      <TableCell>
                        {schedule.isAdHoc ? (
                          <Chip label={t('schedules.adHoc')}
                            color="info"
                            size="small" />
                        ) : (
                          <Chip label={schedule.isActive ? t('common.active') : t('common.inactive')}
                            color={schedule.isActive ? 'success' : 'default'}
                            size="small" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton onClick={(e) => { e.stopPropagation(); handleTriggerSchedule(schedule); }}
                          disabled={saving || triggering}
                          title={t('schedules.triggerNow')}
                          color="primary"
                          size="small">
                          <TriggerIcon />
                        </IconButton>
                        {!schedule.isAdHoc && (
                          <IconButton onClick={(e) => { e.stopPropagation(); handleToggleActive(schedule); }}
                            disabled={saving}
                            title={schedule.isActive ? t('schedules.deactivate') : t('schedules.activate')}
                            size="small">
                            {schedule.isActive ? <PauseIcon /> : <ActivateIcon />}
                          </IconButton>
                        )}
                        <IconButton onClick={(e) => { e.stopPropagation(); handleEditSchedule(schedule); }}
                          disabled={saving}
                          title={t('schedules.editSchedule')}
                          size="small">
                          <EditIcon />
                        </IconButton>
                        <IconButton onClick={(e) => { e.stopPropagation(); handleDeleteSchedule(schedule); }}
                          disabled={saving}
                          color="error"
                          title={t('schedules.deleteSchedule')}
                          size="small">
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <CustomPagination page={page}
              setPage={setPage}
              maxPages={maxPages()}
              breakpointForChangeDisplay={120000}
              useNoDataMessage={false} />
          </>
        )}
      </Section>

      {/* -----------------------------
          Schedule Dialog
      ----------------------------- */}
      <Dialog open={scheduleDialogOpen}
        onClose={() => setScheduleDialogOpen(false)}
        maxWidth="md"
        fullWidth>
        <DialogTitle>{selectedSchedule ? t('schedules.editSchedule') : t('schedules.createSchedule')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            {selectedTemplateFranchises.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 6 }}>
                {selectedTemplateFranchises.map((f, index) => {
                  const colorKey = ['green', 'blue', 'violet'][index % 3];
                  const pallet = colorKey === 'green'
                    ? theme.palette.success
                    : theme.palette.custom[colorKey as 'blue' | 'violet'];
                  return (
                    <Pill key={f.franchiseId}
                      text={f.franchiseName}
                      pallet={pallet} />
                  );
                })}
              </Box>
            )}

            {/* Template */}
            <FormControl fullWidth
              required>
              <InputLabel>{t('schedules.auditTemplate')}</InputLabel>
              <Select value={form.templateId}
                onChange={(e) => handleTemplateChange(Number(e.target.value))}
                label={t('schedules.auditTemplate')}>
                <MenuItem value={0}>{t('schedules.selectTemplate')}</MenuItem>
                {publishedTemplates.map((template) => <MenuItem key={template.id}
                  value={template.id}>{template.name}</MenuItem>)}
              </Select>
            </FormControl>

            {/* Store/Region Assignment */}
            <Box>
              <Stack spacing={1.5}>
                <FormControl fullWidth
                  size="small">
                  <Select
                    value={form.assignmentType}
                    onChange={(e) => {
                      const newType = e.target.value as 'all' | 'stores' | 'regions';
                      setForm({ ...form, assignmentType: newType, storeNumbers: [], regionIds: [], assignToUserIds: [] });
                      if (newType === 'regions' || newType === 'stores') {
                        setFilteredAuditors([]);
                      } else {
                        setAuditorsLoading(true);
                        const franchiseIds = templateFranchiseIdsRef.current;
                        complianceService.auditors.getAll(franchiseIds.length > 0 ? franchiseIds : undefined)
                          .then(setFilteredAuditors)
                          .catch(() => setFilteredAuditors([]))
                          .finally(() => setAuditorsLoading(false));
                      }
                    }}
                    displayEmpty
                    disabled={form.templateId === 0 || templateDataLoading}
                  >
                    <MenuItem value="all">{t('schedules.allStores')}</MenuItem>
                    <MenuItem value="stores">{t('schedules.specificStores')}</MenuItem>
                    <MenuItem value="regions">{t('schedules.byRegion')}</MenuItem>
                  </Select>
                </FormControl>

                {form.assignmentType === 'stores' && (
                  <Autocomplete
                    multiple
                    options={storeOptions}
                    value={selectedStores}
                    getOptionLabel={(option) => `${option.storeName} (${getDisplayStoreNumber(option)})`}
                    isOptionEqualToValue={(option, value) => option.storeNumber === value.storeNumber}
                    onChange={(_e, newValue) => {
                      setSelectedStores(newValue);
                      const newStoreNumbers = newValue.map(s => s.storeNumber);
                      setForm({ ...form, storeNumbers: newStoreNumbers, assignToUserIds: [] });
                      loadAuditorsForStores(newStoreNumbers);
                    }}
                    onInputChange={handleStoreSearchInput}
                    onOpen={() => searchStores('')}
                    loading={storesLoading}
                    disabled={form.templateId === 0 || templateDataLoading}
                    disableCloseOnSelect
                    filterOptions={(x) => x}
                    renderOption={(props, option, { selected }) => (
                      <li {...props}
                        key={option.id}>
                        <Checkbox checked={selected}
                          sx={{ mr: 1 }} />
                        <ListItemText
                          primary={option.storeName}
                          secondary={`${option.storeNumber} - ${option.region}`}
                        />
                      </li>
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((store, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={store.storeNumber}
                          label={`${store.storeName} (${store.storeNumber})`}
                          size="small"
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        placeholder={form.storeNumbers.length === 0 ? t('schedules.searchStores') : ''}
                      />
                    )}
                    noOptionsText={t('schedules.noStoresFound')}
                  />
                )}

                {form.assignmentType === 'regions' && (
                  <FormControl fullWidth
                    size="small">
                    <Select
                      multiple
                      value={form.regionIds}
                      onChange={(e) => {
                        const newRegionIds = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                        setForm({ ...form, regionIds: newRegionIds, assignToUserIds: [] });
                        loadAuditorsForRegions(newRegionIds);
                      }}
                      displayEmpty
                      renderValue={(selected) => selected.length === 0 ? t('schedules.selectRegions') : (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((regionId) => {
                            const region = filteredRegions.find(r => String(r.id) === String(regionId));
                            return <Chip key={regionId}
                              label={region ? region.name : regionId}
                              size="small" />;
                          })}
                        </Box>
                      )}
                    >
                      {filteredRegions.map((region) => (
                        <MenuItem key={region.id}
                          value={region.id}>
                          <Checkbox checked={form.regionIds.indexOf(region.id) > -1} />
                          <ListItemText primary={region.name}
                            secondary={[
                              region.franchiseName,
                              `${region.storeCount} ${region.storeCount === 1 ? t('common.store') : t('schedules.stores')}`,
                            ].filter(Boolean).join(' \u2022 ')} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Stack>
            </Box>

            {/* Auditor Assignment */}
            <FormControl fullWidth
              required>
              <InputLabel>{t('schedules.assignAuditors')}</InputLabel>
              <Select
                multiple
                value={form.assignToUserIds}
                onChange={(e) => setForm({ ...form, assignToUserIds: typeof e.target.value === 'string' ? [] : e.target.value })}
                label={t('schedules.assignAuditors')}
                disabled={form.templateId === 0 || templateDataLoading || auditorsLoading || (form.assignmentType === 'regions' && form.regionIds.length === 0) || (form.assignmentType === 'stores' && form.storeNumbers.length === 0)}
                endAdornment={(templateDataLoading || auditorsLoading) ? <CircularProgress size={20}
                  sx={{ mr: 2 }} /> : undefined}
                MenuProps={{
                  PaperProps: { style: { maxHeight: 300 } },
                  disablePortal: false,
                }}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((userId) => {
                      const auditor = filteredAuditors.find(a => a.id === userId);
                      return <Chip key={userId}
                        label={auditor ? auditor.email : `User ${userId}`}
                        size="small"
                        sx={{ maxWidth: 150 }} />;
                    })}
                  </Box>
                )}
              >
                {filteredAuditors.map((auditor) => (
                  <MenuItem key={auditor.id}
                    value={auditor.id}>
                    <Checkbox checked={form.assignToUserIds.indexOf(auditor.id) > -1} />
                    <ListItemText primary={auditor.name}
                      secondary={`${auditor.role} - ${auditor.email}`} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              control={<Switch checked={form.isAdHoc}
                onChange={(e) => setForm({ ...form, isAdHoc: e.target.checked, ...(e.target.checked ? { cronExpression: '', isActive: true } : { cronExpression: '0 0 0 ? * * *' }) })} />}
              label={t('schedules.adHocLabel')}
            />

            {!form.isAdHoc && (
              <>
                <Box>
                  <Typography variant="textSM"
                    sx={{ mb: 1 }}>{t('schedules.recurrence')}</Typography>
                </Box>

                <CronExpressionPicker
                  value={form.cronExpression}
                  onChange={(cron) => setForm({ ...form, cronExpression: cron })}
                  hideTimeFields />
              </>
            )}

            {/* Active & Notifications */}
            {!form.isAdHoc && (
              <FormControlLabel
                control={<Switch checked={form.isActive}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />}
                label={t('schedules.activeScheduleLabel')}
              />
            )}
            <FormControlLabel
              control={<Checkbox checked={form.notifyOnCreate}
                onChange={(e) => setForm({ ...form, notifyOnCreate: e.target.checked })} />}
              label={t('schedules.notifyOnCreate')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="secondary"
            onClick={() => setScheduleDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="primary"
            onClick={handleSaveSchedule}
            disabled={
              saving
              || form.templateId === 0
              || (!form.isAdHoc && !form.cronExpression)
              || form.assignToUserIds.length === 0
              || (form.assignmentType === 'stores' && form.storeNumbers.length === 0)
              || (form.assignmentType === 'regions' && form.regionIds.length === 0)
            }>{selectedSchedule ? t('schedules.updateSchedule') : t('schedules.createSchedule')}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <ConfirmationDeleteDialog
        open={deleteDialogOpen}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setSelectedSchedule(null);
        }}
        nameOfObject={selectedSchedule?.templateName || t('schedules.schedule')}
      />

      {/* Trigger Now Confirmation Dialog */}
      <Dialog open={triggerDialogOpen}
        onClose={() => { setTriggerDialogOpen(false); setTriggerSchedule(null); }}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>{t('schedules.triggerNowTitle')}</DialogTitle>
        <DialogContent>
          <Typography
            variant="textSM"
            sx={{ mt: 1 }}>
            {t('schedules.triggerNowConfirm', {
              template: triggerSchedule?.templateName || '',
              store: triggerSchedule?.storeName || t('schedules.allStores'),
            })}
          </Typography>
          <Typography
            variant="textSM"
            color="text.secondary"
            sx={{ mt: 1 }}>
            {t('schedules.triggerAdhocNote')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="secondary"
            onClick={() => { setTriggerDialogOpen(false); setTriggerSchedule(null); }}
            disabled={triggering}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary"
            onClick={handleConfirmTrigger}
            disabled={triggering}>
            {triggering ? <CircularProgress size={20} /> : t('schedules.triggerNow')}
          </Button>
        </DialogActions>
      </Dialog>
    </PageArea>
  );
};

export default AuditSchedules;
